import { useMemo } from "react";
import { useLocation, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  ArrowLeft,
  CheckCircle2,
  Clock3,
  Info,
  TrendingUp,
} from "lucide-react";
import { ProgramDataProxy } from "@entities/program/api/data.proxy";
import { ProgramWeek } from "@entities/program/types/base";
import { getWeeksAmount } from "@entities/program/lib/weeksAmount";
import {
  WorkoutHistoryExerciseData,
  WorkoutHistoryExerciseSetData,
  WorkoutHistoryProxy,
} from "@entities/training";
import { MuscleService } from "@entities/muscle";
import type { ExerciseParams } from "@pages/TrainingConstructor/types";
import { ExercisesAccordion } from "@widgets/trainingModals/formTrainingModal/exercisesAccordion";
import { CustomLoader } from "@shared/animations/loaders/CustomLoader";
import { getMediaRoleUrl } from "@shared/helpers/getMediaUrl";
import { useGoBack } from "@shared/hooks/UseGoBack";
import { DefaultPage } from "@shared/layout/DefaultPage";
import {
  ProgramCompactHeader,
  ProgramCompactHeaderSentinel,
  useProgramCompactHeaderVisibility,
} from "@pages/program/ui/ProgramCompactHeader";
import "@widgets/trainingModals/formTrainingModal/trainingFormModal.css";
import "./program-training-history.css";

type RouteParams = {
  enrollmentId: string;
  weekNumber: string;
  dayNumber: string;
};

type ExerciseDetails = NonNullable<
  ProgramWeek["days"][number]["workout"]
>["exercises"][number];

const parseNumber = (value: string | null | undefined): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const plural = (value: number, forms: [string, string, string]): string => {
  const mod10 = value % 10;
  const mod100 = value % 100;

  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];
  return forms[2];
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "Дата не указана";
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "Дата не указана";

  return date.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return "Время не указано";
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "Время не указано";

  return `${date.toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })} в ${date.toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

const getDurationMinutes = (
  startedAt: string | null | undefined,
  completedAt: string | null | undefined,
  exercises: WorkoutHistoryExerciseData[],
): number => {
  if (startedAt && completedAt) {
    const started = new Date(startedAt).getTime();
    const completed = new Date(completedAt).getTime();
    const diffMs = completed - started;

    if (Number.isFinite(diffMs) && diffMs > 0) {
      return Math.max(1, Math.round(diffMs / 60000));
    }
  }

  const seconds = exercises.reduce((total, exercise) => {
    const setCount = Math.max(
      exercise.sets?.length ?? exercise.sets_target ?? 1,
      1,
    );
    const workSeconds =
      exercise.type === "cardio" ? (exercise.duration ?? 40) : 45;
    const restSeconds = exercise.rest_time ?? 30;
    return total + setCount * (workSeconds + restSeconds);
  }, 0);

  return Math.max(1, Math.round(seconds / 60));
};

const getPlanDaysAmount = (week?: ProgramWeek | null): number => {
  const amount = week?.days?.length ?? 0;
  return amount > 0 ? amount : 7;
};

const getProgramFormat = (
  week?: ProgramWeek | null,
  program?: Awaited<ReturnType<typeof ProgramDataProxy.getEnrollmentById>>,
): string => {
  const daysPerWeek = getPlanDaysAmount(week);
  const weeksAmount = getWeeksAmount(program);
  return `${daysPerWeek}x${weeksAmount || week?.week_num || 1}`;
};

const findExerciseDetails = (
  historyExercise: WorkoutHistoryExerciseData,
  workout?: ProgramWeek["days"][number]["workout"],
): ExerciseDetails | undefined => {
  const workoutExercises = workout?.exercises?.filter(Boolean);

  return workoutExercises?.find((exercise) => {
    return (
      exercise.exercise_detail?.id_exercise === historyExercise.exercise_id ||
      exercise.order === historyExercise.order
    );
  });
};

const getExerciseName = (
  historyExercise: WorkoutHistoryExerciseData,
  details?: ExerciseDetails,
): string =>
  details?.exercise_detail?.name ?? `Упражнение ${historyExercise.order}`;

const getExerciseImage = (details?: ExerciseDetails): string | undefined => {
  return getMediaRoleUrl(details?.exercise_detail?.media, [
    "cover",
    "icon",
    "muscle_groups",
  ]);
};

const makeFallbackSets = (
  historyExercise: WorkoutHistoryExerciseData,
  details?: ExerciseDetails,
): WorkoutHistoryExerciseSetData[] => {
  const count = Math.max(
    historyExercise.sets_target ??
      details?.sets ??
      historyExercise.sets?.length ??
      1,
    1,
  );

  return Array.from({ length: count }, () => ({}));
};

const getExerciseSets = (
  historyExercise: WorkoutHistoryExerciseData,
  details?: ExerciseDetails,
): WorkoutHistoryExerciseSetData[] => {
  if (Array.isArray(historyExercise.sets) && historyExercise.sets.length > 0) {
    return historyExercise.sets;
  }

  return makeFallbackSets(historyExercise, details);
};

const getCardioWorkTime = (
  set: WorkoutHistoryExerciseSetData,
  exercise: WorkoutHistoryExerciseData,
  details?: ExerciseDetails,
): number =>
  set.duration ??
  set.work_time_sec ??
  exercise.duration ??
  details?.duration ??
  40;

const getCardioRestTime = (
  set: WorkoutHistoryExerciseSetData,
  exercise: WorkoutHistoryExerciseData,
  details?: ExerciseDetails,
): number =>
  set.rest_time ??
  set.rest_time_sec ??
  exercise.rest_time ??
  details?.rest_time ??
  20;

const getStrengthReps = (
  set: WorkoutHistoryExerciseSetData,
  exercise: WorkoutHistoryExerciseData,
  details?: ExerciseDetails,
): number => set.reps ?? exercise.reps_target ?? details?.reps ?? 0;

const getStrengthWeight = (set: WorkoutHistoryExerciseSetData): number => {
  if (typeof set.weight === "number" && Number.isFinite(set.weight)) {
    return set.weight;
  }

  return 0;
};

const mapHistoryExerciseToParams = (
  historyExercise: WorkoutHistoryExerciseData,
  details?: ExerciseDetails,
): ExerciseParams => {
  const muscleGroups = MuscleService.getMuscleLabelsParentsInfo(
    details?.exercise_detail?.muscle_group_codes ?? [],
  );
  const actualSets = getExerciseSets(historyExercise, details);
  const baseParams = {
    exerciseName: getExerciseName(historyExercise, details),
    exercise_id:
      details?.exercise_detail?.id_exercise ?? historyExercise.exercise_id,
    instanceId: `history-${historyExercise.exercise_id}-${historyExercise.order}`,
    order: historyExercise.order,
    imageUrl: getExerciseImage(details),
    primaryMuscle: muscleGroups[0]?.name,
    secondaryMuscles: muscleGroups.slice(1, 4).map((muscle) => muscle.name),
    actualSets,
  };

  if (historyExercise.type === "cardio") {
    const firstSet = actualSets[0];

    return {
      ...baseParams,
      type: "cardio",
      duration: firstSet
        ? getCardioWorkTime(firstSet, historyExercise, details)
        : (historyExercise.duration ?? details?.duration ?? undefined),
      rest_time: firstSet
        ? getCardioRestTime(firstSet, historyExercise, details)
        : (historyExercise.rest_time ?? details?.rest_time ?? undefined),
    };
  }

  const firstSet = actualSets[0];

  return {
    ...baseParams,
    type: "strength",
    sets:
      actualSets.length ||
      historyExercise.sets_target ||
      details?.sets ||
      undefined,
    reps: firstSet
      ? getStrengthReps(firstSet, historyExercise, details)
      : (historyExercise.reps_target ?? details?.reps ?? undefined),
    duration: historyExercise.duration ?? details?.duration ?? undefined,
    rest_time: historyExercise.rest_time ?? details?.rest_time ?? undefined,
    actualSets: actualSets.map((set) => ({
      ...set,
      reps: getStrengthReps(set, historyExercise, details),
      weight: getStrengthWeight(set),
    })),
  };
};

const WorkoutHistoryProgramCard = ({
  workoutTitle,
  programFormat,
  dayNumber,
  totalDays,
  durationMinutes,
  exercisesAmount,
}: {
  workoutTitle: string;
  programFormat: string;
  dayNumber: number;
  totalDays: number;
  durationMinutes: number;
  exercisesAmount: number;
}) => (
  <section
    className="program-history-program-card"
    aria-label="Информация о тренировке"
  >
    <div className="program-history-program-main">
      <span className="program-history-format">{programFormat}</span>
      <span className="program-history-label">Тренировка</span>
      <strong>{workoutTitle}</strong>
      <span className="program-history-day">
        День {dayNumber} из {totalDays}
      </span>
    </div>
    <div
      className="program-history-program-metrics"
      aria-label="Метрики тренировки"
    >
      <div className="program-history-metric">
        <span className="program-history-metric-icon is-purple">
          <Clock3 aria-hidden="true" />
        </span>
        <span>Длительность</span>
        <strong>{durationMinutes} мин</strong>
      </div>
      <div className="program-history-metric">
        <span className="program-history-metric-icon is-purple">
          <Activity aria-hidden="true" />
        </span>
        <span>Упражнений</span>
        <strong>{exercisesAmount}</strong>
      </div>
    </div>
  </section>
);

const WorkoutHistoryStatusCard = ({
  completedAt,
  progressPercent,
}: {
  completedAt: string | null | undefined;
  progressPercent: number;
}) => (
  <section
    className="program-history-status-card"
    aria-label="Статус тренировки"
  >
    <div className="program-history-status-item">
      <span className="program-history-status-icon">
        <CheckCircle2 aria-hidden="true" />
      </span>
      <div>
        <strong>Тренировка выполнена</strong>
        <span>{formatDateTime(completedAt)}</span>
      </div>
    </div>
    <div className="program-history-status-item">
      <span className="program-history-status-icon">
        <TrendingUp aria-hidden="true" />
      </span>
      <div>
        <strong>Прогресс</strong>
        <b>{progressPercent}%</b>
      </div>
    </div>
  </section>
);

export const ProgramTrainingHistoryPage = () => {
  const { enrollmentId, weekNumber, dayNumber } = useParams<RouteParams>();
  const location = useLocation();
  const goBackToCalendar = useGoBack(`/program/${enrollmentId}/calendar`);
  const {
    isCompactHeaderVisible,
    compactHeaderRootRef,
    compactHeaderSentinelRef,
  } = useProgramCompactHeaderVisibility();
  const search = new URLSearchParams(location.search);
  const queryHistoryId = parseNumber(search.get("workoutHistoryId"));
  const numericDayNumber = parseNumber(dayNumber) ?? 1;

  const weekQuery = useQuery({
    queryKey: ["programWeek", enrollmentId, `weekNumber/${weekNumber}`],
    queryFn: async () => ProgramDataProxy.getWeekInfo(enrollmentId, weekNumber),
    enabled: Boolean(enrollmentId && weekNumber),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const programQuery = useQuery({
    queryKey: ["enrollment", enrollmentId],
    queryFn: async () => ProgramDataProxy.getEnrollmentById(+enrollmentId),
    enabled: Boolean(enrollmentId),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 30_000,
  });

  const selectedDay = useMemo(
    () => weekQuery.data?.days?.find((day) => day.day_num === numericDayNumber),
    [weekQuery.data?.days, numericDayNumber],
  );
  const resolvedHistoryId =
    queryHistoryId ??
    selectedDay?.workout_history_id ??
    selectedDay?.workout?.history?.id_workout_history ??
    null;

  const historyQuery = useQuery({
    queryKey: ["workoutHistory", resolvedHistoryId],
    queryFn: async () =>
      WorkoutHistoryProxy.getWorkoutHistoryById(resolvedHistoryId ?? 0),
    enabled: Boolean(resolvedHistoryId),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const isResolvingHistoryId = !queryHistoryId && weekQuery.isLoading;
  const isLoading = isResolvingHistoryId || historyQuery.isLoading;
  const history = historyQuery.data;
  const exercises = useMemo(
    () =>
      [...(history?.exercises_data ?? [])].sort((a, b) => a.order - b.order),
    [history?.exercises_data],
  );
  const exerciseParams = useMemo(
    () =>
      exercises.map((exercise) =>
        mapHistoryExerciseToParams(
          exercise,
          findExerciseDetails(exercise, selectedDay?.workout),
        ),
      ),
    [exercises, selectedDay?.workout],
  );
  const programTitle =
    weekQuery.data?.program_name ||
    programQuery.data?.program_name ||
    "Программа тренировок";
  const workoutTitle = selectedDay?.workout?.name || programTitle;
  const programFormat = getProgramFormat(weekQuery.data, programQuery.data);
  const totalDays = getPlanDaysAmount(weekQuery.data);
  const completedAt =
    history?.completed_at ?? selectedDay?.completed_at ?? null;
  const progressPercent = history ? 100 : 0;
  const durationMinutes = history
    ? getDurationMinutes(history.started_at, completedAt, exercises)
    : 0;
  const historyDateLabel = formatDate(completedAt ?? selectedDay?.due_at);

  return (
    <DefaultPage noPadding style={{ background: "#f8f8f3" }}>
      <main className="h-full w-full max-w-full overflow-hidden bg-[#F8F8F3] text-[#111111]">
        <section className="relative flex h-full flex-col overflow-hidden">
          <ProgramCompactHeader
            isVisible={isCompactHeaderVisible}
            onBack={goBackToCalendar}
            title="История тренировки"
            rightLabel={programFormat}
            details={
              <span className="min-w-0 truncate">
                {historyDateLabel} · День {numericDayNumber}
              </span>
            }
          />
          <div className="program-history-screen" ref={compactHeaderRootRef}>
            <div className="relative">
              <ProgramCompactHeaderSentinel
                ref={compactHeaderSentinelRef}
                style={{ top: "calc(104px - 18px - env(safe-area-inset-top))" }}
              />
              <header className="program-history-header">
                <button
                  type="button"
                  className="program-history-back"
                  aria-label="Назад"
                  onClick={goBackToCalendar}
                >
                  <ArrowLeft aria-hidden="true" />
                </button>
                <div>
                  <h1>История тренировки</h1>
                  <p>
                    {historyDateLabel} · День {numericDayNumber}
                  </p>
                </div>
              </header>

              {isLoading ? (
                <div className="program-history-loader">
                  <CustomLoader />
                </div>
              ) : !resolvedHistoryId || !history ? (
                <section className="program-history-empty">
                  <h2>История недоступна</h2>
                  <p>
                    Для этого дня пока не удалось получить фактические
                    результаты тренировки.
                  </p>
                </section>
              ) : (
                <div className="program-history-content">
                  <WorkoutHistoryProgramCard
                    workoutTitle={workoutTitle}
                    programFormat={programFormat}
                    dayNumber={numericDayNumber}
                    totalDays={totalDays}
                    durationMinutes={durationMinutes}
                    exercisesAmount={
                      history.exercises_total || exercises.length
                    }
                  />
                  <WorkoutHistoryStatusCard
                    completedAt={completedAt}
                    progressPercent={progressPercent}
                  />
                  <section className="program-history-exercises-section">
                    <h2>
                      {history.exercises_total || exercises.length}{" "}
                      {plural(history.exercises_total || exercises.length, [
                        "упражнение",
                        "упражнения",
                        "упражнений",
                      ])}
                    </h2>
                    <ExercisesAccordion
                      exerciseParams={exerciseParams}
                      setExerciseParams={() => undefined}
                      addedExercises={exerciseParams}
                      onDelete={() => undefined}
                      isReadonly
                      readonlySummaryLabel="Общий результат"
                      className="program-history-exercises-list"
                    />
                  </section>
                  <div className="program-history-info-banner">
                    <Info aria-hidden="true" />
                    <span>
                      Данные показывают фактические результаты выполненной
                      тренировки.
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
    </DefaultPage>
  );
};
